import { ManageFootballCompetition } from "../../../commons/models/football/admin/manage-football-competition";
import { FootballBasicStatistics } from "../../../commons/models/football/football-basic-statistics";
import { FootballCompetitionTeam } from "../../../commons/models/football/football-competition-team";
import { FootballGroupedStanding } from "../../../commons/models/football/football-grouped-standing";
import { FootballSeason } from "../../../commons/models/football/football-season";
import { AbstractBaseService } from "../../../ensue-react-system/utilities/base-service";
import { EnsueCollection } from "../../../ensue-react-system/utilities/ensue-collection";
import { PaginatedEnsueCollection } from "../../../ensue-react-system/utilities/paginated-ensue-collection";
import { FootballTournamentManager } from "../../../commons/models/football/football-tournament-manager";
import { ManageFootballTeam } from "../../../commons/models/football/admin/manage-football-team";
import { ManageFootballCompetitionInvitedTeam } from "../../../commons/models/football/admin/manage-football-competition-invited-team";

export interface ICompetitionDTO {
  title: string;
  abbreviation: string;
  description: string | null;
  logo: string | null;
  type: string;
  teamGender: number | null;
  ageGroup: string | null;
  teamForm: string | null;
  skillLevel: string | null;
  organizerId: number | null;
}

export interface ISeasonInfo {
  title: string;
  startAt: string;
  endAt: string;
  status: string;
}

export interface ISeasonEndInfo {
  endAt: string;
}

export interface ISeasonTeamsInfo {
  teamIds: number[];
}

export class ManageFootballCompetitionService extends AbstractBaseService<ManageFootballCompetition> {
  protected __model: ManageFootballCompetition = new ManageFootballCompetition();

  public getResourceName(): string {
    return "manage/football/competitions";
  }

  public addCompetition(competitionData: ICompetitionDTO) {
    return this.__client.post(this.__url, competitionData);
  }

  public editCompetition(competitionId: number, competitionData: ICompetitionDTO) {
    return this.__client.patch(this.__url + "/" + competitionId, competitionData);
  }

  public getCompetitionSeasonList(
    competitionId: number,
    urlParams?: { [key: string]: string | number | boolean }
  ): Promise<PaginatedEnsueCollection<FootballSeason>> {
    const seasonListModel = new FootballSeason();
    const options = this.createHttpParamObject(urlParams);
    return this.__client
      .get(this.__url + "/" + competitionId + "/seasons", options)
      .then((obj) => seasonListModel.createFromCollection(obj) as PaginatedEnsueCollection<FootballSeason>);
  }

  public getCompetitionSeason(
    competitionId: number,
    seasonId: number,
    urlParams?: { [key: string]: string | number | boolean }
  ): Promise<FootballSeason> {
    const options = this.createHttpParamObject(urlParams);
    const seasonModel = new FootballSeason();
    return this.__client
      .get(this.__url + "/" + competitionId + "/seasons/" + seasonId, options)
      .then((obj) => seasonModel.create(obj) as FootballSeason);
  }

  public setCompetitionSeason(
    competitionId: number,
    seasonInfo: ISeasonInfo,
    urlParams?: { [key: string]: string | number | boolean }
  ) {
    const options = this.createHttpParamObject(urlParams);
    return this.__client.post(this.__url + "/" + competitionId + "/seasons", seasonInfo, options);
  }

  public editCompetitionSeason(
    competitionId: number,
    seasonId: number,
    seasonInfo: ISeasonInfo,
    urlParams?: { [key: string]: string | number | boolean }
  ) {
    const options = this.createHttpParamObject(urlParams);
    return this.__client.patch(this.__url + "/" + competitionId + "/seasons/" + seasonId, seasonInfo, options);
  }

  public endCompetitionSeason(
    competitionId: number,
    seasonId: number,
    seasonEndInfo: ISeasonEndInfo,
    urlParams?: { [key: string]: string | number | boolean }
  ) {
    const options = this.createHttpParamObject(urlParams);
    return this.__client.patch(
      this.__url + "/" + competitionId + "/seasons/" + seasonId + "/end",
      seasonEndInfo,
      options
    );
  }

  public getAvailableTeamList(
    competitionId: number,
    seasonId: number,
    urlParams?: { [key: string]: string | number | boolean }
  ): Promise<EnsueCollection<ManageFootballTeam>> {
    const teamListModel = new ManageFootballTeam();
    const options = this.createHttpParamObject(urlParams);
    return this.__client
      .get(this.__url + "/" + competitionId + "/seasons/" + seasonId + "/available-teams", options)
      .then((obj) => teamListModel.createFromCollection(obj) as EnsueCollection<ManageFootballTeam>);
  }

  public getTeamList(
    competitionId: number,
    seasonId: number,
    urlParams?: { [key: string]: string | number | boolean }
  ): Promise<EnsueCollection<FootballCompetitionTeam>> {
    const teamListModel = new FootballCompetitionTeam();
    const options = this.createHttpParamObject(urlParams);
    return this.__client
      .get(this.__url + "/" + competitionId + "/seasons/" + seasonId + "/teams", options)
      .then((obj) => teamListModel.createFromCollection(obj) as EnsueCollection<FootballCompetitionTeam>);
  }

  public addSeasonTeams(
    competitionId: number,
    seasonId: number,
    seasonTeamsInfo: ISeasonTeamsInfo,
    urlParams?: { [key: string]: string | number | boolean }
  ): Promise<EnsueCollection<FootballCompetitionTeam>> {
    const options = this.createHttpParamObject(urlParams);
    return this.__client.post(
      this.__url + "/" + competitionId + "/seasons/" + seasonId + "/teams",
      seasonTeamsInfo,
      options
    );
  }

  public removeSeasonTeam(
    competitionId: number,
    seasonId: number,
    teamId: number,
    urlParams?: { [key: string]: string | number | boolean }
  ): Promise<EnsueCollection<FootballCompetitionTeam>> {
    const options = this.createHttpParamObject(urlParams);
    return this.__client.delete(
      this.__url + "/" + competitionId + "/seasons/" + seasonId + "/teams/" + teamId,
      options
    );
  }

  public getStandingList(
    competitionId: number,
    seasonId: number,
    urlParams?: { [key: string]: string | number | boolean }
  ): Promise<FootballGroupedStanding> {
    const options = this.createHttpParamObject(urlParams);
    return this.__client.get(this.__url + "/" + competitionId + "/seasons/" + seasonId + "/standings", options);
  }

  public getCompetitionSeasonStatistics(
    competitionId: number,
    seasonId: number,
    urlParams?: { [key: string]: string | number | boolean }
  ): Promise<FootballBasicStatistics> {
    const statisticsModal = new FootballBasicStatistics();
    const options = this.createHttpParamObject(urlParams);
    return this.__client
      .get(this.__url + "/" + competitionId + "/seasons/" + seasonId + "/statistics", options)
      .then((obj) => statisticsModal.create(obj) as FootballBasicStatistics);
  }

  public async getTournamentManagers(tournamentId: number): Promise<EnsueCollection<FootballTournamentManager>> {
    return this.__client.get(`${this.__url}/${tournamentId}/managers`);
  }

  public async addRemoveTournamentManager(
    tournamentId: number,
    userId: number,
    remove = false
  ): Promise<FootballTournamentManager> {
    const url = `${this.__url}/${tournamentId}/users/${userId}/roles/managers`;
    if (remove) {
      return this.__client.delete(url);
    }
    return this.__client.post(url, null);
  }

  public getAvailableInviteTeamList(
    competitionId: number,
    seasonId: number,
    urlParams?: { [key: string]: string | number | boolean }
  ): Promise<EnsueCollection<ManageFootballTeam>> {
    const teamListModel = new ManageFootballTeam();
    const options = this.createHttpParamObject(urlParams);
    return this.__client
      .get(this.__url + "/" + competitionId + "/seasons/" + seasonId + "/available-invite-teams", options)
      .then((obj) => teamListModel.createFromCollection(obj) as EnsueCollection<ManageFootballTeam>);
  }

  public getInvitedTeamList(
    competitionId: number,
    seasonId: number,
    urlParams?: { [key: string]: string | number | boolean }
  ): Promise<EnsueCollection<ManageFootballCompetitionInvitedTeam>> {
    const teamListModel = new ManageFootballCompetitionInvitedTeam();
    const options = this.createHttpParamObject(urlParams);
    return this.__client
      .get(this.__url + "/" + competitionId + "/seasons/" + seasonId + "/invite-teams", options)
      .then((obj) => teamListModel.createFromCollection(obj) as EnsueCollection<ManageFootballCompetitionInvitedTeam>);
  }

  public setTeamInvite(
    competitionId: number,
    seasonId: number,
    teamsInfo: ISeasonTeamsInfo,
    urlParams?: { [key: string]: string | number | boolean }
  ) {
    const options = this.createHttpParamObject(urlParams);
    return this.__client.post(
      this.__url + "/" + competitionId + "/seasons/" + seasonId + "/invite-teams",
      teamsInfo,
      options
    );
  }
}
